/**
 * Daily token budget indicator for the chat input footer (§7g).
 *
 * Reads tokenUsage from the Zustand store and renders an abbreviated
 * "1.2k / 50k tokens used today" line. Consumes data provided by 7f's
 * GET /api/usage endpoint via `loadUsage` in the store.
 *
 * Polling cadence (per §7.8 scoping Q4): load on mount; the store's
 * finishStreaming / finishRoundtableTurn actions call loadUsage()
 * after every completed turn, so the footer re-renders automatically.
 * No setInterval — the chat is the only thing that moves the needle.
 *
 * Colour thresholds:
 *   < 80%  — neutral (default text colour)
 *   ≥ 80%  — amber (warning)
 *   ≥ 95%  — red (danger)
 *
 * Renders nothing when tokenUsage is null (still loading / endpoint
 * unreachable) — the footer is informational, not critical.
 */

import { useEffect } from "react";
import { useStore } from "../store/useStore.js";

function abbreviate(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

export default function BudgetFooter() {
  const usage = useStore((s) => s.tokenUsage);
  const loadUsage = useStore((s) => s.loadUsage);

  // Initial load on mount. Subsequent refreshes are triggered by the
  // store's finishStreaming / finishRoundtableTurn actions.
  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  if (!usage) return null;

  const { tokensUsedToday, dailyTokenBudget } = usage;
  if (!dailyTokenBudget) return null;

  const pct = tokensUsedToday / dailyTokenBudget;
  const tone = pct >= 0.95 ? "danger" : pct >= 0.8 ? "warning" : "neutral";

  return (
    <div className={`budget-footer budget-footer-${tone}`}>
      <span className="budget-footer-label">
        {abbreviate(tokensUsedToday)} / {abbreviate(dailyTokenBudget)} tokens
        used today
      </span>
    </div>
  );
}
